import { makeAutoObservable } from 'mobx';

export type PageResult<T> = {
  content: T[];
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  size: number;
  totalElements: number;
  totalPages: number;
  readonly empty: boolean;
  map<U>(callback: (elem: T, index: number) => U): U[];
  merge(result: PageResult<T>): PageResult<T>;
};

export function createPageResult<T>(json?: Partial<PageResult<T>>): PageResult<T> {
  return makeAutoObservable({
    content: json?.content ?? [],
    first: json?.first ?? true,
    last: json?.last ?? true,
    number: json?.number ?? 0,
    numberOfElements: json?.numberOfElements ?? 0,
    size: json?.size ?? 0,
    totalElements: json?.totalElements ?? 0,
    totalPages: json?.totalPages ?? 0,

    get empty(): boolean {
      return this.content.length === 0;
    },

    map<U>(callback: (elem: T, index: number) => U): U[] {
      return this.content.map(callback);
    },

    merge(result: PageResult<T>): PageResult<T> {
      if (this.number !== result.number || this.empty) {
        return createPageResult<T>({
          number: result.number,
          content: this.content.concat(result.content),
          first: result.first,
          last: result.last,
        });
      }
      return this;
    },
  });
}

export const PageResultApi = {
  execute<T>(
    response: Promise<T[]>,
    counter: Promise<number>,
    page: number = 0,
    size: number = 10,
  ): Promise<PageResult<T>> {
    return Promise.all([response, counter]).then(([result, count]) => {
      const last = result.length < size;
      const totalPages = Math.ceil(count / size);
      return createPageResult({
        content: result,
        first: page === 0,
        last,
        totalPages,
        number: page,
        size,
        totalElements: count,
      });
    });
  },
};
